import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormProvider, useForm } from "react-hook-form";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { z } from "zod";
import { useAuth } from "../../context/AuthProvider";
import FormInput from "../forms/FormInput";

const registerSchema = z
  .object({
    username: z.string().min(1, "Name must be at least 1 characters"),
    password: z.string().min(7, "Password must be at least 7 characters long"),
    verifyPassword: z
      .string()
      .min(7, "Password must be at least 7 characters long"),
    // The 18+ check itself is the server's; it answers with the reason.
    dateOfBirth: z.string().min(1, "Enter your date of birth"),
  })
  .refine((data) => data.password === data.verifyPassword, {
    message: "Passwords must match",
    path: ["verifyPassword"], // Targets the confirmPassword field
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { register: registerUser } = useAuth();
  const methods = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: "onBlur",
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, touchedFields, isSubmitting, isValid },
  } = methods;
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmit = async (data: RegisterFormData) => {
    try {
      if (!executeRecaptcha) {
        toast("Please complete the CAPTCHA!");
        return;
      }
      const { username, password, dateOfBirth } = data;
      // CHECK CATPCHA
      const recaptchaToken = await executeRecaptcha("submit");
      const success = await registerUser(
        username,
        password,
        recaptchaToken,
        dateOfBirth
      );
      if (success) {
        navigate("/Home");
      } else {
        reset({ verifyPassword: "" });
      }

      // make register request
    } catch (error) {
      toast.error(`Error: ${error}`);
    }
  };
  return (
    <div className="bg-base-200 h-full flex flex-col justify-center items-center">
      <div className="card bg-base-100 m-2 w-full shadow-lg sm:w-96">
        <div className="card-body">
          <h2 className="card-title ">Register</h2>
          <div className="divider divider-primary m-0" />
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <FormInput
                label="Username"
                name="username"
                type="text"
                register={register("username")}
                errors={errors.username}
                touchedFields={touchedFields}
              />
              <FormInput
                label="Password"
                name="password"
                type="password"
                register={register("password")}
                errors={errors.password}
                touchedFields={touchedFields}
              />
              <FormInput
                label="Verify Password"
                icon={
                  <div
                    onClick={() => setShowPassword(!showPassword)}
                    className={`btn btn-sm btn-ghost ${
                      showPassword ? "text-primary" : ""
                    }`}
                  >
                    {showPassword ? (
                      <FiEye size={20} />
                    ) : (
                      <FiEyeOff size={20} />
                    )}
                  </div>
                }
                name="verifyPassword"
                type={showPassword ? "text" : "password"}
                register={register("verifyPassword")}
                errors={errors.verifyPassword}
                touchedFields={touchedFields}
              />
              <FormInput
                label="Date of birth (you must be 18 or older)"
                name="dateOfBirth"
                type="date"
                register={register("dateOfBirth")}
                errors={errors.dateOfBirth}
                touchedFields={touchedFields}
              />

              <button
                type="submit"
                className="btn btn-primary text-white w-full"
                disabled={isSubmitting || !isValid}
              >
                {isSubmitting ? "Saving..." : "Register"}
              </button>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
